import LumaAccessiblePanel from '../admin/CoworkFormPanel';
import {Dialog as LumaAccessibleDialog, DialogTitle as LumaAccessibleTitle} from '@headlessui/react';
import React, { useState, useEffect } from 'react';
import { X, Image as ImageIcon, Sparkles } from 'lucide-react';
import { LandingEspacio, createEspacio, updateEspacio } from '../../services/landing/landingSectionsService';

interface EspacioModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    espacioToEdit?: LandingEspacio | null;
}

export const EspacioModal: React.FC<EspacioModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    espacioToEdit,
}) => {
    const [label, setLabel] = useState('');
    const [alt, setAlt] = useState('');
    const [urlImagen, setUrlImagen] = useState('');
    const [imagenFile, setImagenFile] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [orden, setOrden] = useState<number>(0);
    const [activo, setActivo] = useState(true);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (espacioToEdit) {
            setLabel(espacioToEdit.label);
            setAlt(espacioToEdit.alt || '');
            setUrlImagen(espacioToEdit.url_imagen || '');
            setOrden(espacioToEdit.orden);
            setActivo(espacioToEdit.activo);
            setPreviewUrl(espacioToEdit.url_imagen || espacioToEdit.imagen || null);
            setImagenFile(null);
        } else {
            setLabel('');
            setAlt('');
            setUrlImagen('');
            setOrden(0);
            setActivo(true);
            setImagenFile(null);
            setPreviewUrl(null);
        }
        setError(null);
    }, [espacioToEdit, isOpen]);

    if (!isOpen) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImagenFile(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);
        if (!label.trim()) {
            setError('El nombre del espacio es obligatorio');
            return;
        }

        try {
            setLoading(true);
            const formData = new FormData();
            formData.append('label', label.trim());
            formData.append('alt', alt.trim() || label.trim());
            formData.append('url_imagen', urlImagen.trim());
            formData.append('orden', String(orden));
            formData.append('activo', activo ? 'true' : 'false');

            if (imagenFile) {
                formData.append('imagen', imagenFile);
            }

            if (espacioToEdit) {
                await updateEspacio(espacioToEdit.id, formData);
            } else {
                await createEspacio(formData);
            }

            onSuccess();
            onClose();
        } catch (err: any) {
            /* Browser diagnostics omitted. */
            setError(err.response?.data?.detail || 'Error al guardar el espacio de la galería');
        } finally {
            setLoading(false);
        }
    };

    return (
        <LumaAccessibleDialog open={isOpen} onClose={onClose} data-luma-overlay="true" className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in overflow-y-auto">
            <LumaAccessiblePanel data-luma-dialog="true" className="relative w-full max-w-lg bg-white dark:bg-[#120a21] rounded-2xl shadow-2xl border border-gray-100 dark:border-purple-900/40 p-6 my-8">
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-purple-950/40 transition-colors"
                >
                    <X size={18} />
                </button>

                <div className="flex items-center gap-3 mb-6">
                    <div className="p-3 bg-gradient-to-tr from-[#3564db]/20 to-[#3564db]/20 text-[#3564db] rounded-xl border border-purple-500/20">
                        <Sparkles size={22} />
                    </div>
                    <div>
                        <LumaAccessibleTitle className="text-xl font-bold text-gray-900 dark:text-white">
                            {espacioToEdit ? 'Editar Espacio del Acordeón' : 'Nuevo Espacio (Galería)'}
                        </LumaAccessibleTitle>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                            Fotografía y etiqueta de la sección interactiva "Sobre Nosotros"
                        </p>
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 rounded-xl text-red-600 dark:text-red-400 text-xs">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4"><div className="luma-form-intro"><span>01</span><div><h3>Información del registro</h3><p>Completa los campos del formulario.</p></div></div>
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Nombre del Espacio *
                        </label>
                        <input
                            type="text"
                            value={label}
                            onChange={(e) => setLabel(e.target.value)}
                            placeholder="Ej: Sala de reuniones ejecutiva"
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#3564db]"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Texto alternativo (Accesibilidad SEO)
                        </label>
                        <input
                            type="text"
                            value={alt}
                            onChange={(e) => setAlt(e.target.value)}
                            placeholder="Ej: Vista panorámica del espacio de reuniones"
                            className="w-full px-4 py-2.5 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#3564db]"
                        />
                    </div>

                    {/* Subida o URL de Imagen */}
                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            Fotografía del Espacio
                        </label>
                        <div className="mt-1 flex justify-center px-6 pt-4 pb-5 border-2 border-gray-300 dark:border-purple-900/50 border-dashed rounded-xl hover:border-[#3564db] transition-colors bg-gray-50/50 dark:bg-purple-950/10">
                            <div className="space-y-2 text-center">
                                {previewUrl ? (
                                    <div className="relative inline-block">
                                        <img
                                            src={previewUrl}
                                            alt="Vista previa"
                                            className="h-36 w-full object-cover rounded-lg shadow-md mx-auto"
                                        />
                                        <span className="block mt-2 text-xs text-purple-600 font-semibold">
                                            {imagenFile ? `Archivo: ${imagenFile.name}` : 'Imagen actual'}
                                        </span>
                                    </div>
                                ) : (
                                    <ImageIcon className="mx-auto h-10 w-10 text-gray-400" />
                                )}
                                <div className="flex text-sm text-gray-600 dark:text-gray-400 justify-center">
                                    <label className="relative cursor-pointer rounded-md font-semibold text-[#3564db] dark:text-purple-400 hover:underline">
                                        <span>Subir foto desde tu dispositivo</span>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            className="sr-only"
                                            onChange={handleFileChange}
                                        />
                                    </label>
                                </div>
                                <p className="text-xs text-gray-500">PNG, JPG, WEBP hasta 10MB</p>
                            </div>
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                            O URL Externa de la Imagen (opcional si subes archivo)
                        </label>
                        <input
                            type="text"
                            value={urlImagen}
                            onChange={(e) => {
                                setUrlImagen(e.target.value);
                                if (!imagenFile && e.target.value) setPreviewUrl(e.target.value);
                            }}
                            placeholder="https://images.unsplash.com/..."
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-xs focus:outline-none focus:border-[#3564db]"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4 items-center pt-2">
                        <div>
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                                Posición / Orden
                            </label>
                            <input
                                type="number"
                                min={0}
                                value={orden}
                                onChange={(e) => setOrden(Number(e.target.value))}
                                className="w-full px-4 py-2 rounded-xl border border-gray-200 dark:border-purple-900/40 bg-white dark:bg-[#190f2b] text-gray-900 dark:text-white text-sm focus:outline-none focus:border-[#3564db]"
                            />
                        </div>
                        <div className="flex items-center gap-3 pt-4">
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={activo}
                                    onChange={(e) => setActivo(e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-purple-950 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#3564db]"></div>
                            </label>
                            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                                {activo ? 'Publicado' : 'Oculto'}
                            </span>
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-purple-900/30">
                        <button
                            type="button"
                            onClick={onClose}
                            disabled={loading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-purple-950/40 rounded-xl transition-colors disabled:opacity-50"
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className="px-6 py-2 text-sm font-semibold text-white bg-gradient-to-r from-[#3564db] to-[#3564db] hover:opacity-95 rounded-xl shadow-lg shadow-purple-900/20 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                            {loading ? (
                                <>
                                    <span className="inline-block w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                                    Guardando...
                                </>
                            ) : espacioToEdit ? (
                                'Actualizar Espacio'
                            ) : (
                                'Guardar Espacio'
                            )}
                        </button>
                    </div>
                </form>
            </LumaAccessiblePanel>
        </LumaAccessibleDialog>
    );
};

